"use client";

import { useCallback, useEffect, useMemo } from "react";
import { MessageKind } from "@/types/enums/message";
import { type MessageDraft, useMessageDraftStore } from "@/stores/message-draft-store";
import { useChatTipTapEditorHeightStore } from "@/stores/tiptap-editor-height-store";
export type { MessageDraft } from "@/stores/message-draft-store";
interface UseChatDraftOptions {
  chatId: string | null | undefined;
}
interface UseChatDraftReturn {
  draftContent: string;
  draft: MessageDraft | null;
  handleDraftChange: (text: string) => void;
  clearDraft: () => void;
}
export function useChatDraft({
  chatId
}: UseChatDraftOptions): UseChatDraftReturn {
  const {
    useDraftContent,
    handleDraftChange: storeDraftChange,
    clearDraftOnSend,
    getDraft
  } = useMessageDraftStore();
  const {
    setHeight
  } = useChatTipTapEditorHeightStore();
  const draftContent = useDraftContent(chatId);
  const draft = chatId ? getDraft(chatId) as MessageDraft | null : null;

  // Reset height when chatId becomes null/undefined
  useEffect(() => {
    if (!chatId) {
      setHeight(0);
    }
  }, [chatId, setHeight]);
  const handleDraftChange = useCallback((text: string) => {
    storeDraftChange(chatId, text, draft?.message ?? {
      kind: MessageKind.DEFAULT
    });
  }, [chatId, storeDraftChange, draft?.message]);
  const clearDraft = useCallback(() => {
    if (chatId) {
      clearDraftOnSend(chatId);
    }
  }, [chatId, clearDraftOnSend]);
  return useMemo(() => ({
    draftContent,
    draft,
    handleDraftChange,
    clearDraft
  }), [draftContent, draft, handleDraftChange, clearDraft]);
}